import { get } from '@js/common/api'
import { UA } from '@js/common/UA'
import User from '@js/common/user'
import {
  isFinanceRelatedType,
  isHouseRelatedType,
  isOutfitterRelatedType,
  isVendorRelatedType
} from '@js/common/utils'

import { isNotEmptyArray } from '@f-fertighaus/applications/Next/utils/typeguards/isNotEmptyArray'
import { WIZARD_REQUEST_TYPES } from '@f-fertighaus/applications/RequestWizard/partials/wizardConstants'
import HouseBuyerStage from '@f-fertighaus/applications/RequestWizard/steps/HouseBuyerStage'
import SendRequest from '@f-fertighaus/applications/RequestWizard/steps/SendRequest'
import UserAddress from '@f-fertighaus/applications/RequestWizard/steps/UserAddress'
import UserAddressPhone from '@f-fertighaus/applications/RequestWizard/steps/UserAddressPhone'
import UserConstructionStart from '@f-fertighaus/applications/RequestWizard/steps/UserConstructionStart'
import UserEmailPhone from '@f-fertighaus/applications/RequestWizard/steps/UserEmailPhone'
import UserGenderNameEmail from '@f-fertighaus/applications/RequestWizard/steps/UserGenderNameEmail'
import UserName from '@f-fertighaus/applications/RequestWizard/steps/UserName'
import UserProperty from '@f-fertighaus/applications/RequestWizard/steps/UserProperty'

import { ExposeDownload } from '../steps/ExposeDownload/ExposeDownload'
import { UserActivation } from '../steps/UserActivation/UserActivation'

export const getFlowTypeByRequestType = requestType => {
  switch (requestType) {
    case WIZARD_REQUEST_TYPES.houseDetailForm:
    case WIZARD_REQUEST_TYPES.houseDetailGallery:
    case WIZARD_REQUEST_TYPES.houseListForm:
      return 'house-wizard-item'

    case WIZARD_REQUEST_TYPES.vendorDetailForm:
    case WIZARD_REQUEST_TYPES.vendorDetailOfficesForm:
      return 'vendor-wizard-item'

    case WIZARD_REQUEST_TYPES.outfitter:
      return 'outfitter-wizard-item'
  }
}

const getExplicitWizardStepsByRequestType = type => {
  if (type === WIZARD_REQUEST_TYPES.outfitter) {
    return [UserName, UserEmailPhone, UserAddress]
  }

  if (isHouseRelatedType(type)) {
    if (type === WIZARD_REQUEST_TYPES.houseDetailFavourite) {
      return [UserGenderNameEmail, UserActivation]
    }
    if (type === WIZARD_REQUEST_TYPES.houseDetailExpose) {
      return [UserGenderNameEmail, ExposeDownload]
    }
    return [
      UserGenderNameEmail,
      UserAddressPhone,
      UserProperty,
      UserConstructionStart,
      HouseBuyerStage
    ]
  }

  return getVendorWizardSteps()
}

const getStepsWithoutFirstStep = steps =>
  steps.filter((item, index) => index !== 0)

export const getWizardStepsByRequestType = (requestType, conversionFunnel) => {
  const userState = UA.getCurrentUserState()
  const isConversionFunnel = Boolean(conversionFunnel)

  const shouldSkipFirstStep =
    isHouseRelatedType(requestType) && userState && isConversionFunnel

  if (shouldSkipFirstStep) {
    return getStepsWithoutFirstStep([
      ...getExplicitWizardStepsByRequestType(requestType),
      SendRequest
    ])
  }

  return [...getExplicitWizardStepsByRequestType(requestType), SendRequest]
}

export const getInitialWebTrackingValueByRequestType = requestType => {
  if (requestType === WIZARD_REQUEST_TYPES.outfitter) {
    return 'gender-name'
  }
  if (isHouseRelatedType(requestType)) {
    return 'soft-question'
  }
  return 'property'
}

export const computeHeadingTitle = (requestType, displayName) => {
  switch (requestType) {
    case WIZARD_REQUEST_TYPES.houseDetailForm:
    case WIZARD_REQUEST_TYPES.houseDetailGallery:
    case WIZARD_REQUEST_TYPES.houseListForm:
      return `Jetzt kostenlose Infos zu diesem Haus von ${displayName} anfordern`

    case WIZARD_REQUEST_TYPES.vendorDetailForm:
    case WIZARD_REQUEST_TYPES.vendorDetailOfficesForm:
      return `Kostenlose Infos anfordern von ${displayName}`

    default:
      return 'Kostenlose Infos anfordern'
  }
}

export const getConversionFromFlowInfo = (flowInfo, _wtIndicates) => {
  if (isHouseRelatedType(flowInfo)) return _wtIndicates || 'house-conversion'
  if (isVendorRelatedType(flowInfo)) return _wtIndicates || 'house-conversion'
  if (isFinanceRelatedType(flowInfo)) return 'finance-conversion'
  if (isOutfitterRelatedType(flowInfo)) return 'outfitter-conversion'
  return 'could-not-match-conversion-type-in-user-address-jsx'
}

export const conversionFunnelCopy = {
  'house-wizard-item-priceinfo': {
    step_1: {
      header: 'Jetzt Konto erstellen und kostenlose Preisdetails erhalten',
      ctaText: 'Kostenloses Konto erstellen'
    },
    step_2: {
      header: 'Wohin dürfen wir Ihnen die Infomaterialien senden?',
      ctaText: 'Jetzt Preisdetails anfordern'
    }
  },
  'house-wizard-item-infomaterial': {
    step_1: {
      header: 'Jetzt Konto erstellen und kostenlose Infos erhalten',
      ctaText: 'Kostenloses Konto erstellen'
    },
    step_2: {
      header: 'Wohin dürfen wir Ihnen die Infomaterialien senden?',
      ctaText: 'Kostenlose Infos anfordern'
    }
  },
  'house-wizard-item-configuration': {
    step_1: {
      header: 'Wie können wir Sie zur Beratung kontaktieren?',
      ctaText: 'Absenden & weiter'
    },
    step_2: {
      header: 'Wie erreichen wir Sie zur Terminvereinbarung?',
      ctaText: 'Kostenlose Beratung vereinbaren'
    }
  },
  'house-wizard-item-offer': {
    step_1: {
      header: 'Jetzt Konto erstellen und persönliches Angebot erhalten',
      ctaText: 'Absenden & weiter'
    },
    step_2: {
      header: 'Wohin dürfen wir Ihnen die Infomaterialien senden?',
      ctaText: 'Persönliches Angebot anfordern'
    }
  },
  'house-wizard-item-comparison': {
    step_1: {
      header: 'Jetzt kostenloses Konto erstellen und Angebote vergleichen',
      ctaText: 'Kostenloses Konto erstellen'
    },
    step_2: {
      header: 'Wohin dürfen wir Ihnen die Infomaterialien senden?',
      ctaText: 'Angebote vergleichen'
    }
  },
  'house-wizard-item-catalog': {
    step_1: {
      header: 'Jetzt Konto erstellen und kostenlosen Katalog erhalten',
      ctaText: 'Kostenloses Konto erstellen'
    },
    step_2: {
      header: 'Wohin dürfen wir Ihnen den Katalog senden?',
      ctaText: 'Kostenlosen Katalog anfordern'
    }
  },
  'house-wizard-item-appointment': {
    step_1: {
      header: 'Für wen ist der unverbindliche Beratungstermin?',
      ctaText: 'Absenden & weiter'
    },
    step_2: {
      header: 'Wie erreichen wir Sie zur Terminvereinbarung?',
      ctaText: 'Kostenlose Beratung vereinbaren'
    }
  },
  'house-wizard-item-favourite': {
    step_1: {
      header: 'Jetzt Konto erstellen und kostenlose Infos erhalten',
      ctaText: 'Kostenloses Konto erstellen'
    },
    step_2: {
      header: 'Wohin dürfen wir Ihnen die Infomaterialien senden?',
      ctaText: 'Kostenlose Infos anfordern'
    }
  },
  'house-wizard-item-expose': {
    step_1: {
      header: 'Melden Sie sich an, um das kostenlose Exposé zu erhalten',
      ctaText: 'Konto erstellen & herunterladen'
    },
    step_2: {
      header: 'Vielen Dank für Ihr Interesse am kostenlosen Exposé',
      ctaText: 'Exposé herunterladen'
    }
  },
  'vendor-wizard-item': {
    step_1: {
      header: 'An wen dürfen die kostenlosen Infos gesendet werden?',
      ctaText: 'Weiter'
    },
    step_2: {
      header:
        'Wie können wir für Rückfragen am besten mit Ihnen in Verbindung treten?',
      ctaText: 'Absenden & Weiter'
    },
    step_3: {
      header: 'An welche Adresse dürfen Infomaterialien gesendet werden?',
      ctaText: 'Weiter'
    }
  }
}

export const computePersonalInfoPayload = async (initialPayload, uaData) => {
  const payload = {
    email: initialPayload.email || uaData.email,
    first_name: initialPayload.first_name || uaData.first_name,
    last_name: initialPayload.last_name || uaData.last_name,
    gender: initialPayload.gender || uaData.gender
  }
  const emptyFields = Object.entries(payload).filter(([, value]) => !value)

  if (isNotEmptyArray(emptyFields)) {
    const { current_user } = await get('users/current')
    emptyFields.forEach(([key]) => {
      payload[key] = current_user[key]
    })
  }

  return payload
}

export const composeLeadAssignmentRequestData = (house, location, user) => {
  const leadAssignmentParams = {}

  if (house.living_area_total) {
    leadAssignmentParams.sqm = house.living_area_total
  }

  let expansion_stage = 'price_ready'
  if (house.living_area_total && !house.price_ready) {
    if (house.price_fitting) {
      expansion_stage = 'price_fitting'
    } else if (house.price_lego) {
      expansion_stage = 'price_lego'
    }
  }

  leadAssignmentParams.delivery_region = location.postcode

  if (user.postal_code) {
    leadAssignmentParams.postal_code = user.country + '-' + user.postal_code
  }

  const constructionMethods = User.getHouseSearchParam('construction_method', {
    within: []
  }).within

  leadAssignmentParams.construction_methods = constructionMethods
    .map(
      method =>
        ({
          1: 'prefab',
          2: 'solid',
          3: 'blockhouse'
        }[method])
    )
    .filter(Boolean)
    .join(',')
  leadAssignmentParams.urgency = user.construction_start
  leadAssignmentParams.property = user.property || 'no'
  leadAssignmentParams.budget = Math.floor(house[expansion_stage])
  leadAssignmentParams.request_location = 'hard_request'
  leadAssignmentParams.distinct_id = window['🍪'].read('nr_distinct_id')
  leadAssignmentParams.expansion_stage = expansion_stage

  return leadAssignmentParams
}

export const getWizardRelevantUserData = () => {
  const WIZARD_RELEVANT_FIELDS = [
    'first_name',
    'last_name',
    'email',
    'gender',
    'phone',
    'construction_start',
    'property'
  ]
  return Object.entries(UA.getLS()).reduce((acc, [key, value]) => {
    if (WIZARD_RELEVANT_FIELDS.includes(key) && value) {
      acc[key] = value
    }
    return acc
  }, {})
}

const getVendorWizardSteps = () => {
  const userState = UA.getLS()
  const vendorSteps = []

  if (!userState?.property) {
    vendorSteps.push(UserProperty)
  }
  if (!userState?.construction_start) {
    vendorSteps.push(UserConstructionStart)
  }
  if (!userState?.gender || !userState?.first_name || !userState?.last_name) {
    vendorSteps.push(UserName)
  }
  if (!userState?.email || !userState?.phone) {
    vendorSteps.push(UserEmailPhone)
  }
  vendorSteps.push(UserAddress)

  return vendorSteps
}
